import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faHeadset,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Modal,
} from "@themesberg/react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Routes } from "../../routes";

// Replace this with your actual logo asset
// import BrandLogo from "../../assets/img/brand/brandlogo.jpg";
import BrandLogo from "../../assets/img/brand/Brand_Logo.png";
import { changePassword, forgotPasswordsendOtp } from "../api";

// const RESET_PASSWORD_API_URL = "/auth/reset-password";

const otpRegex = /^\d{6}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getLoginPath = () => {
  return Routes?.Signin?.path || "/sign-in";
};

const getSupportPath = () => {
  return Routes?.Support?.path || "#";
};

const maskEmail = (email) => {
  const value = String(email || "").trim();
  const atIndex = value.indexOf("@");

  if (atIndex <= 1) return value;

  const name = value.slice(0, atIndex);
  const domain = value.slice(atIndex);

  if (name.length <= 2) {
    return `${name[0]}***${domain}`;
  }

  return `${name[0]}${"*".repeat(Math.max(name.length - 2, 3))}${name[name.length - 1]}${domain}`;
};

const getPasswordRules = (password) => {
  const value = String(password || "");
  return {
    uppercase: /[A-Z]/.test(value),
    lowercase: /[a-z]/.test(value),
    number: /\d/.test(value),
    special: /[@$!%*?&]/.test(value),
    minLength: value.length >= 8,
  };
};

const isPasswordValid = (password) => {
  const rules = getPasswordRules(password);
  return Object.values(rules).every(Boolean);
};

export default function ChangePassword() {
  const navigate = useNavigate();
  const location = useLocation();

  const loginId = location?.state?.loginId || "";
  const maskedEmailFromState = location?.state?.maskedEmail || "";

  const [formData, setFormData] = useState({
    otp: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  const [cooldownSeconds, setCooldownSeconds] = useState(Number(location?.state?.cooldownSeconds || 0));
  const [maskedEmail, setMaskedEmail] = useState(
    maskedEmailFromState || maskEmail(loginId)
  );

  const trimmedOtp = useMemo(() => formData.otp.trim(), [formData.otp]);
  const trimmedNewPassword = useMemo(() => formData.newPassword.trim(), [formData.newPassword]);
  const trimmedConfirmNewPassword = useMemo(
    () => formData.confirmNewPassword.trim(),
    [formData.confirmNewPassword]
  );

  const passwordRules = useMemo(
    () => getPasswordRules(formData.newPassword),
    [formData.newPassword]
  );

  useEffect(() => {
    if (cooldownSeconds <= 0) return;

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownSeconds]);

  const openModal = (type, message = "") => {
    setActiveModal(type);
    setModalMessage(message);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalMessage("");
  };

const handleChange = (e) => {
  const { name, value } = e.target;

  setFormData((prev) => {
    const nextForm = {
      ...prev,
      [name]: value,
    };

    const nextPassword = name === "newPassword" ? value : prev.newPassword;
    const nextConfirmPassword =
      name === "confirmNewPassword" ? value : prev.confirmNewPassword;

    setFieldErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };

      if (name === "newPassword") {
        nextErrors.newPassword = value.trim()
          ? isPasswordValid(value)
            ? ""
            : "Password must meet all the rules below."
          : "New password is required.";

        nextErrors.confirmNewPassword = nextConfirmPassword
          ? nextPassword !== nextConfirmPassword
            ? "Passwords do not match."
            : ""
          : prevErrors.confirmNewPassword;
      }

      if (name === "confirmNewPassword") {
        nextErrors.confirmNewPassword = value.trim()
          ? nextPassword !== value
            ? "Passwords do not match."
            : ""
          : "Confirm new password is required.";
      }

      if (name === "otp") {
        nextErrors.otp = "";
      }

      return nextErrors;
    });

    return nextForm;
  });
};

  const resendOtp = async () => {
    try {
      setSubmitting(true);

      const response = await forgotPasswordsendOtp({
        email: loginId,
      });

      const data = response?.data || {};

      setMaskedEmail(data?.maskedEmail || maskedEmail || maskEmail(loginId));
      setCooldownSeconds(Number(data?.cooldownSeconds || 30));
      setFormData((prev) => ({
        ...prev,
        otp: "",
      }));

      openModal("otpResent", "A new OTP has been sent to your registered email.");
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Unable to resend OTP. Please try again.";

      if (status === 429) {
        openModal("tooManyOtpRequests", message);
      } else {
        openModal("technicalError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const validateBeforeSubmit = () => {
    const errors = {};

    if (!trimmedOtp) {
      errors.otp = "OTP is required.";
    } else if (!otpRegex.test(trimmedOtp)) {
      errors.otp = "Enter a valid 6-digit OTP.";
    }

    if (!trimmedNewPassword) {
      errors.newPassword = "New password is required.";
    } else if (!isPasswordValid(trimmedNewPassword)) {
      errors.newPassword = "Password must meet all the rules below.";
    }

    if (!trimmedConfirmNewPassword) {
      errors.confirmNewPassword = "Confirm new password is required.";
    } else if (trimmedNewPassword !== trimmedConfirmNewPassword) {
      errors.confirmNewPassword = "Passwords do not match.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit()) return;

    try {
      setSubmitting(true);

      const response = await changePassword({
        email: loginId,
        otp: trimmedOtp,
        password: trimmedNewPassword,
      });

      const data = response?.data || {};

      if (data?.status === "REUSE_NOT_ALLOWED") {
        openModal("passwordReuseNotAllowed", "New password cannot be the same as old password.");
        return;
      }

      openModal("passwordResetSuccess", "Password saved successfully.");
      setTimeout(() => {
        navigate(getLoginPath());
      }, 1200);
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Password reset failed. Please try again.";

      if (status === 409) {
        openModal("passwordReuseNotAllowed", message);
      } else if (status === 400) {
        openModal("passwordPolicyFailed", message);
      } else if (status === 401 || status === 410) {
        openModal("invalidOtp", message);
      } else {
        openModal("technicalError", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main style={{ background: "#f4f8fb", minHeight: "100vh" }}>
      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 shadow-sm rounded-4" style={{ background: "#ffffff" }}>
                <Card.Body className="p-4 p-md-5">
                  <div className="text-center mb-4">
                    <img
                      src={BrandLogo}
                      alt="Brand logo"
                      style={{
                        maxWidth: "17rem",
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  </div>

                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-0">Change Password</h4>
                  </div>

                  <Form noValidate onSubmit={handleResetPassword}>
                    <Form.Group className="mb-3" controlId="maskedEmail">
                      <Form.Label className="fw-semibold">Registered Email</Form.Label>
                      <Form.Control
                        type="text"
                        value={maskedEmail}
                        readOnly
                        className="py-2"
                        placeholder="Registered email"
                        disabled
                        style={{ cursor: "not-allowed" }}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="otp">
                      <Form.Label className="fw-semibold">
                        OTP <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap ${
                          fieldErrors.otp ? "invalid-input-group" : ""
                        }`}
                      >
                        <InputGroup.Text className="bg-white">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          name="otp"
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter 6-digit OTP"
                          value={formData.otp}
                          onChange={(e) => {
                            const nextValue = e.target.value.replace(/\D/g, "").slice(0, 6);
                            setFormData((prev) => ({
                              ...prev,
                              otp: nextValue,
                            }));
                            setFieldErrors((prev) => ({
                              ...prev,
                              otp: "",
                            }));
                          }}
                          isInvalid={!!fieldErrors.otp}
                          className="py-2 border-start-0 border-end-0"
                          style={{ boxShadow: "none" }}
                        />
                        <InputGroup.Text
                          className="p-0"
                          style={{
                            background: "#e9ecef",
                            minWidth: "110px",
                            justifyContent: "center",
                            borderColor: "#dee2e6",
                          }}
                        >
                          <Button
                            type="button"
                            variant="link"
                            onClick={resendOtp}
                            disabled={submitting || cooldownSeconds > 0}
                            className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                              background: "transparent",
                              color: "#5f6b7a",
                              minHeight: "46px",
                              fontSize: "0.9rem",
                            }}
                          >
                            {cooldownSeconds > 0 ? `Resend (${cooldownSeconds})` : "Resend OTP"}
                          </Button>
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {fieldErrors.otp}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="newPassword">
                      <Form.Label className="fw-semibold">
                        New Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap ${
                          fieldErrors.newPassword ? "invalid-input-group" : ""
                        }`}
                      >
                        <InputGroup.Text className="bg-white">
                          <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>

                        <Form.Control
                          name="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          placeholder="Enter New Password"
                          value={formData.newPassword}
                          onChange={handleChange}
                          isInvalid={!!fieldErrors.newPassword}
                          className="py-2 border-start-0 border-end-0"
                          style={{ boxShadow: "none" }}
                        />

                        <InputGroup.Text
                          className="p-0"
                          style={{
                            background: "#e9ecef",
                            minWidth: "58px",
                            justifyContent: "center",
                            borderColor: "#dee2e6",
                          }}
                        >
                          <Button
                            type="button"
                            variant="link"
                            onClick={() => setShowNewPassword((prev) => !prev)}
                            className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                              background: "transparent",
                              color: "#5f6b7a",
                              minHeight: "46px",
                            }}
                          >
                            <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                          </Button>
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {fieldErrors.newPassword}
                      </Form.Control.Feedback>

                      {formData.newPassword && (
                        <ul className="mt-2 mb-0 ps-4" style={{ color: "#dc3545" }}>
                          {!passwordRules.uppercase && <li>At least 1 uppercase letter</li>}
                          {!passwordRules.lowercase && <li>At least 1 lowercase letter</li>}
                          {!passwordRules.number && <li>At least 1 number</li>}
                          {!passwordRules.special && <li>At least 1 special character (@$!%*?&)</li>}
                          {!passwordRules.minLength && <li>Minimum 8 characters</li>}
                        </ul>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3" controlId="confirmNewPassword">
                      <Form.Label className="fw-semibold">
                        Confirm New Password <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap ${
                          fieldErrors.confirmNewPassword ? "invalid-input-group" : ""
                        }`}
                      >
                        <InputGroup.Text className="bg-white">
                          <FontAwesomeIcon icon={faLock} />
                        </InputGroup.Text>

                        <Form.Control
                          name="confirmNewPassword"
                          type={showConfirmNewPassword ? "text" : "password"}
                          placeholder="Confirm New Password"
                          value={formData.confirmNewPassword}
                          onChange={handleChange}
                          isInvalid={!!fieldErrors.confirmNewPassword}
                          className="py-2 border-start-0 border-end-0"
                          style={{ boxShadow: "none" }}
                        />

                        <InputGroup.Text
                          className="p-0"
                          style={{
                            background: "#e9ecef",
                            minWidth: "58px",
                            justifyContent: "center",
                            borderColor: "#dee2e6",
                          }}
                        >
                          <Button
                            type="button"
                            variant="link"
                            onClick={() => setShowConfirmNewPassword((prev) => !prev)}
                            className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                            style={{
                              background: "transparent",
                              color: "#5f6b7a",
                              minHeight: "46px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={showConfirmNewPassword ? faEyeSlash : faEye}
                            />
                          </Button>
                        </InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {fieldErrors.confirmNewPassword}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-flex justify-content-center mb-4">
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="px-5 py-2 fw-bold"
                        style={{
                          minWidth: "170px",
                          ...ACTIVE_BUTTON_STYLE,
                        }}
                      >
                        {submitting ? "RESETTING..." : "RESET PASSWORD"}
                      </Button>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                      <Link
                        to={getLoginPath()}
                        className="fw-semibold underline-link"
                        style={{ color: "#005dfb" }}
                      >
                        Back to Login
                      </Link>

                      {maskedEmail ? (
                        <span className="small text-muted">OTP sent to {maskedEmail}</span>
                      ) : null}
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="justify-content-center mt-4 mt-md-5">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 shadow-sm rounded-4" style={{ background: "#ffffff" }}>
                <Card.Body className="p-4 p-md-5">
                  <h4 className="fw-bold mb-2">Need help?</h4>
                  <p className="mb-0 text-muted">
                    Contact your administrator if you cannot reset your password.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Modal show={activeModal === "invalidOtp"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Invalid OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Incorrect OTP entered."}</Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Retry
          </Button>
          <Button className="act-btn" onClick={resendOtp}>
            Resend OTP
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "passwordResetSuccess"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            Password Reset Successful
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Password saved successfully."}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" as={Link} to={getLoginPath()} onClick={closeModal}>
            Go to Login
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "passwordPolicyFailed"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Reset Password Failed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Weak password entered. Please use a stronger password."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "passwordReuseNotAllowed"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
            Password Reuse Not Allowed
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "New password cannot be the same as old password."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "technicalError"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Technical Error
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalMessage || "Something went wrong. Please try again."}</Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={supportModalOpen} onHide={() => setSupportModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHeadset} className="me-2" />
            Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Use your support details here.</p>
          <p className="mb-0 text-muted">
            Example: support@yourcompany.com | +91-XXXXXXXXXX
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSupportModalOpen(false)}>
            Close
          </Button>
          <Button
            className="act-btn"
            as={Link}
            to={getSupportPath()}
            onClick={() => setSupportModalOpen(false)}
          >
            Open Support
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}